import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Info, Settings } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

interface StatusData {
  name: string;
  value: number;
  fill: string;
  status?: string; // Original status value for navigation
  [key: string]: string | number | undefined; // Index signature for Recharts compatibility
}

interface AssetsByStatusCardProps {
  data: StatusData[];
  className?: string;
}

// Green color palette with subtle variance
const GREEN_COLORS = [
  '#10b981', // emerald-500
  '#059669', // emerald-600
  '#34d399', // emerald-400
  '#047857', // emerald-700
  '#6ee7b7', // emerald-300
];

export function AssetsByStatusCard({
  data,
  className,
}: AssetsByStatusCardProps): React.ReactElement {
  const navigate = useNavigate();

  const hasData = Array.isArray(data) && data.length > 0;

  // Assign green colors to each status
  const dataWithGreenColors = hasData 
    ? data.map((item, index) => ({
        ...item,
        color: GREEN_COLORS[index % GREEN_COLORS.length]
      }))
    : [];

  // Calculate max value for scaling
  const maxValue = hasData ? Math.max(...dataWithGreenColors.map(d => d.value)) : 0;
  const totalValue = dataWithGreenColors.reduce((sum, d) => sum + d.value, 0);
  const topStatus = hasData ? dataWithGreenColors.reduce((max, item) => (item.value > max.value ? item : max)) : undefined;

  // Handle bar click to navigate to filtered assets
  const handleStatusClick = (status?: string) => {
    if (status) {
      navigate(`/assets?status=${encodeURIComponent(status)}`);
    }
  };

  if (!hasData) {
    return (
      <Card className={cn('relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black', className)}>
        <CardHeader className="relative">
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Assets by Status</CardTitle>
              <CardDescription>Distribution of asset statuses</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">No status data available.</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn('relative overflow-hidden border-2 shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black', className)}>
      <CardHeader className="relative">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Assets by Status</CardTitle>
            <CardDescription>Distribution of asset statuses</CardDescription>
          </div>
          <div className="flex gap-1">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
                    <Info className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p className="text-sm">
                    This chart shows how your assets are distributed across different statuses: 
                    Active (currently in use), Pending (awaiting deployment), Inactive (not in use), 
                    Maintenance (under repair or service), and Disposed (sold or recycled).
                    <br /><br />
                    <strong>Click on any bar</strong> to view assets for that specific status.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground hover:bg-background/80">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Total Assets Display */}
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold">{totalValue}</div>
            <div className="text-sm text-muted-foreground">Total Assets</div>
          </div>

          {/* Bar Chart */}
          <div className="space-y-4">
            {dataWithGreenColors.map((item, index) => {
              const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
              
              return (
                <div 
                  key={index} 
                  className="space-y-2 cursor-pointer group"
                  onClick={() => handleStatusClick(item.status)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleStatusClick(item.status);
                    }
                  }}
                  aria-label={`View ${item.name} assets`}
                >
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div 
                        className="h-3 w-3 rounded-full transition-transform group-hover:scale-110" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="font-medium group-hover:text-foreground transition-colors">{item.name}</span>
                    </div>
                    <span className="font-semibold group-hover:text-foreground transition-colors">{item.value} assets</span>
                  </div>
                  <div className="relative h-8 w-full overflow-hidden rounded-md bg-muted group-hover:bg-muted/80 transition-colors">
                    <div
                      className="h-full transition-all duration-500 ease-out group-hover:brightness-110"
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: item.color,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Most Common</p>
              <p className="text-sm font-semibold">
                {topStatus?.name ?? '—'}
              </p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Statuses</p>
              <p className="text-sm font-semibold">{dataWithGreenColors.length} types</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
